import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Textarea,
  Input,
} from '@roger/ui-components';
import { searchDiagnosisCodes } from '../services/rogerDocApi';
import { getManualDiagnosisLogMessage } from '../utils/dateUtils';
import { Edit, Loader2, Search, AlertCircle } from 'lucide-react';
import type {
  DiagnosisWithReasoning,
  PrimaryDiagnosis,
  DiagnosisCode,
} from '../types/codingData.types';
import PrimaryFitnessConfirmDialog from './PrimaryFitnessConfirmDialog';
import { DiagnosisCodeValidityWarning } from './DiagnosisCodeValidityWarning';
import { usePrimaryDiagnosisFitness } from '../hooks/usePrimaryDiagnosisFitness';
import {
  fitnessPresentation,
  fitnessUnresolved,
  shouldDiscourage,
} from '../utils/primaryDiagnosisFitness';

interface AddDiagnosisModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (diagnosis: DiagnosisWithReasoning) => void;
  isEditing: boolean;
  isPrimaryDiagnosis: boolean;
  currentDiagnosis: DiagnosisWithReasoning | PrimaryDiagnosis | null;
  diagnosisPosition: string | number | null;
  existingCodes?: string[];
  /**
   * The coding as-of date (M0090, or the visit date), YYYY-MM-DD.
   * Search and validity use the codes valid on that date, so a correction to
   * a September assessment can still pick I42.0.
   */
  codesAsOf?: string;
}

const AddDiagnosisModal: React.FC<AddDiagnosisModalProps> = ({
  open,
  onClose,
  onConfirm,
  isEditing,
  isPrimaryDiagnosis,
  currentDiagnosis,
  diagnosisPosition,
  existingCodes = [],
  codesAsOf,
}) => {
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<DiagnosisCode[]>([]);
  const [searching, setSearching] = useState(false);
  // Roger Docs refused the search (or it failed). Shown as an error, never as
  // "no results", and never with Roger Docs' own text.
  const [searchFailed, setSearchFailed] = useState(false);
  const [pendingPrimary, setPendingPrimary] = useState<DiagnosisCode | null>(
    null
  );
  const [selectedCode, setSelectedCode] = useState<DiagnosisCode | null>(null);

  // This modal searches RogerDoc, whose results carry no PDGM fitness, so ask
  // the EMR about the codes on screen. Fitness is shown in the primary seat
  // only: the CMS acceptability flags say nothing about a secondary. Validity
  // (a code not valid on the coding date) applies to every seat.
  const { fitnessByCode, invalidByCode, fitnessPending } =
    usePrimaryDiagnosisFitness(
      [
        ...searchResults.map(r => r.name),
        ...(selectedCode ? [selectedCode.name] : []),
      ],
      { asOf: codesAsOf }
    );
  // A code the coder picked before its fitness landed. Held rather than judged:
  // see fitnessUnresolved. Cleared the moment the lookup resolves, which is
  // usually before the coder notices it waited.
  const [awaitingFitness, setAwaitingFitness] = useState<DiagnosisCode | null>(
    null
  );
  const [reasoning, setReasoning] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Load current diagnosis data when editing
  useEffect(() => {
    if (open && isEditing && currentDiagnosis) {
      // setSelectedCode(currentDiagnosis.diagnosis);
      // setReasoning(currentDiagnosis.reasoning || '');
      setSearchText('');
      setSearchResults([]);
      setHasSearched(false);
      setAwaitingFitness(null);
    } else if (open && !isEditing) {
      // Reset for adding new
      setSelectedCode(null);
      setReasoning('');
      setSearchText('');
      setSearchResults([]);
      setHasSearched(false);
      setAwaitingFitness(null);
    }
  }, [open, isEditing, currentDiagnosis]);

  const handleSearch = async () => {
    if (!searchText.trim()) return;

    setSearching(true);
    setHasSearched(true);
    setSearchFailed(false);
    try {
      const results = (await searchDiagnosisCodes(searchText, {
        asOf: codesAsOf,
      })) as DiagnosisCode[];
      setSearchResults(results || []);
    } catch (error) {
      console.error('Error searching diagnosis codes:', error);
      setSearchResults([]);
      setSearchFailed(true);
    } finally {
      setSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // One path for every way a search result gets chosen, so a held pick and an
  // immediate one cannot drift apart on what the gate does.
  const chooseCode = React.useCallback(
    (result: DiagnosisCode) => {
      // Seating an unfit code as primary gets the same confirm as everywhere
      // else — discouraged, never blocked, because some payers accept these.
      if (isPrimaryDiagnosis && shouldDiscourage(fitnessByCode[result.name])) {
        setPendingPrimary(result);
        return;
      }
      setSelectedCode(result);
      setSearchResults([]);
      setSearchText('');
      setHasSearched(false);
    },
    [isPrimaryDiagnosis, fitnessByCode]
  );

  // The lookup resolved while a pick was held, so decide now. If it resolved by
  // failing, the code is still absent and chooseCode's fail-closed branch is
  // then the right answer rather than a premature one: we asked, and got
  // nothing back.
  useEffect(() => {
    if (!awaitingFitness || fitnessPending) return;
    const held = awaitingFitness;
    setAwaitingFitness(null);
    chooseCode(held);
  }, [awaitingFitness, fitnessPending, chooseCode]);

  const handleConfirm = () => {
    if (!selectedCode) return;

    // Generate timestamp suffix
    const suffix = `\n\n${getManualDiagnosisLogMessage()}\n\n`;

    onConfirm({
      diagnosis: selectedCode,
      reasoning: suffix + reasoning.trim(),
    });

    // Reset form
    setSelectedCode(null);
    setReasoning('');
    setSearchText('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleClose = () => {
    setSelectedCode(null);
    setReasoning('');
    setSearchText('');
    setSearchResults([]);
    setHasSearched(false);
    // A pick held for its verdict does not survive the modal: resolving it
    // after close would open a confirm over whatever the coder moved on to.
    setAwaitingFitness(null);
    onClose();
  };

  const getTitle = () => {
    if (isEditing && currentDiagnosis) {
      const diagnosis = (currentDiagnosis as DiagnosisWithReasoning).diagnosis;
      const codeAndName = diagnosis
        ? ` ${diagnosis.name} ${diagnosis.desc}`
        : '';
      return isPrimaryDiagnosis
        ? `Replace Primary Diagnosis${codeAndName}`
        : `Replace Secondary Diagnosis #${diagnosisPosition}${codeAndName}`;
    }
    return 'Add Secondary Diagnosis';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-hidden flex flex-col'>
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
          <DialogDescription className='sr-only'>
            Search for and add a diagnosis code to the patient record
          </DialogDescription>
        </DialogHeader>

        <div className='flex-1 overflow-y-auto space-y-4 py-4'>
          {/* Search Section */}
          {!selectedCode && (
            <div className='space-y-3'>
              <label
                htmlFor='search'
                className='block text-sm font-medium text-gray-700'
              >
                Search Diagnosis Code
              </label>
              <div className='flex gap-2'>
                <Input
                  id='search'
                  type='text'
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder='Enter ICD-10 code or description...'
                  className='flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'
                />
                <Button
                  onClick={handleSearch}
                  disabled={searching || !searchText.trim()}
                >
                  {searching ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <Search className='w-4 h-4' />
                  )}
                  Search
                </Button>
              </div>

              {/* Search Results */}
              {hasSearched && (
                <div className='border border-gray-200 rounded-md max-h-96 overflow-y-auto'>
                  {searching ? (
                    <div className='p-4 text-center text-gray-700'>
                      <Loader2 className='w-6 h-6 animate-spin mx-auto mb-2' />
                      Searching...
                    </div>
                  ) : searchFailed ? (
                    <p
                      className='p-4 text-center text-sm text-destructive'
                      role='alert'
                    >
                      Unable to search codes for this date.
                    </p>
                  ) : searchResults.length > 0 ? (
                    <div className='divide-y'>
                      {searchResults.map((result, idx) => {
                        const isDuplicate = existingCodes.includes(result.name);
                        const isChecking =
                          awaitingFitness?.name === result.name;
                        return (
                          <button
                            key={idx}
                            onClick={() => {
                              // Picked before its verdict arrived: hold the
                              // choice rather than judge it. Warning here would
                              // say we checked a code we have not checked.
                              if (
                                isPrimaryDiagnosis &&
                                fitnessUnresolved(
                                  fitnessByCode[result.name],
                                  fitnessPending
                                )
                              ) {
                                setAwaitingFitness(result);
                                return;
                              }
                              chooseCode(result);
                            }}
                            disabled={isDuplicate || isChecking}
                            className={`w-full p-3 text-left transition-colors ${
                              isDuplicate
                                ? 'bg-red-50 cursor-not-allowed opacity-75'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className='flex items-start gap-3'>
                              <div
                                className={`px-2 py-1 text-xs font-mono rounded ${
                                  isDuplicate
                                    ? 'bg-red-200 text-red-800'
                                    : 'bg-primary text-white'
                                }`}
                              >
                                {result.name}
                              </div>
                              <div className='flex-1'>
                                <p
                                  className={`text-sm font-medium ${isDuplicate ? 'text-red-700' : 'text-gray-900'}`}
                                >
                                  {result.desc}
                                </p>
                                {isDuplicate && (
                                  <p className='text-xs text-red-600 mt-1 flex items-center gap-1'>
                                    <AlertCircle className='w-3 h-3' />
                                    This code is already in the chart
                                  </p>
                                )}
                                {isChecking && (
                                  <p className='text-xs text-gray-700 mt-1 flex items-center gap-1'>
                                    <Loader2 className='w-3 h-3 animate-spin' />
                                    Checking this code
                                  </p>
                                )}
                                <DiagnosisCodeValidityWarning
                                  reason={invalidByCode[result.name]}
                                />
                                {isPrimaryDiagnosis &&
                                  (() => {
                                    const mark = fitnessPresentation(
                                      fitnessByCode[result.name]
                                    );
                                    return mark ? (
                                      <p
                                        className={`text-xs mt-1 ${mark.toneClass}`}
                                      >
                                        {mark.message}
                                      </p>
                                    ) : null;
                                  })()}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className='p-4 text-center text-gray-700'>
                      No results found. Try a different search term.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Selected Code Display */}
          {selectedCode && (
            <div className='space-y-4'>
              <div>
                <div className='flex items-center justify-between'>
                  <div className='block text-sm font-medium text-gray-700'>
                    Selected Diagnosis
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setSelectedCode(null)}
                  >
                    <Edit />
                    Change
                  </Button>
                </div>
                <div className='mt-2 p-4 bg-gray-50 border border-gray-200 rounded-md'>
                  <div className='flex items-start gap-3'>
                    <div className='px-3 py-1.5 bg-primary text-white font-mono rounded'>
                      {selectedCode.name}
                    </div>
                    <div className='flex-1'>
                      <p className='font-medium text-gray-900'>
                        {selectedCode.desc}
                      </p>
                      <DiagnosisCodeValidityWarning
                        reason={invalidByCode[selectedCode.name]}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor='reasoning'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Clinical Reasoning
                </label>
                <div className='mb-2 p-3 bg-gray-50 border border-gray-200 rounded-md'>
                  <p className='text-xs text-gray-700 italic'>
                    {getManualDiagnosisLogMessage()}
                  </p>
                </div>
                <Textarea
                  id='reasoning'
                  value={reasoning}
                  onChange={e => setReasoning(e.target.value)}
                  placeholder='Explain why this diagnosis is relevant...'
                  className='mt-2 min-h-[120px]'
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedCode}>
            {isEditing ? 'Save Changes' : 'Add Diagnosis'}
          </Button>
        </DialogFooter>
      </DialogContent>
      <PrimaryFitnessConfirmDialog
        code={pendingPrimary?.name ?? null}
        fitness={
          pendingPrimary ? (fitnessByCode[pendingPrimary.name] ?? null) : null
        }
        onConfirm={() => {
          if (pendingPrimary) {
            setSelectedCode(pendingPrimary);
            setSearchResults([]);
            setSearchText('');
            setHasSearched(false);
          }
          setPendingPrimary(null);
        }}
        onCancel={() => setPendingPrimary(null)}
      />
    </Dialog>
  );
};

export default AddDiagnosisModal;
